import { useEffect, useRef, useState } from 'react';

export type RiskIqAvailability = 'checking' | 'available' | 'unavailable';

// The same lightweight GET the embedded <RiskIq> component itself polls. When the RiskIQ backend's
// own `RiskIQAvailability:Enabled` setting is off, every RiskIQ endpoint returns
// 503 {"status":"unavailable"} - that setting is independent of OPRA's riskIqEnabled UI flag.
const probePath = '/api/protocol-documents/entitlement-status';

const isUnavailableResponse = async (response: Response) => {
    if (response.status !== 503) return false;
    const body: { status?: string } | null = await response.json().catch(() => null);
    return body?.status === 'unavailable';
};

/**
 * Probes RiskIQ's API once per mount, before the embedded <RiskIq> component is rendered, so the
 * page can show a friendly "unavailable" state. The package exposes no error callback/event for
 * this, so without the probe a 503 surfaces as its generic "Something went wrong" alert.
 * Fails open: anything other than 503 {"status":"unavailable"} (incl. network errors) is treated
 * as available and left to the component's own handling. Without an apiOrigin/token there is
 * nothing to probe, so the component is rendered in its own "pending" state.
 * Deliberately not cached (no SWR): a stale "available" would briefly mount the component.
 */
export const useRiskIqAvailability = (apiOrigin?: string, bearerToken?: string): RiskIqAvailability => {
    const canProbe = !!apiOrigin && !!bearerToken;
    const [availability, setAvailability] = useState<RiskIqAvailability>(canProbe ? 'checking' : 'available');
    // Read via a ref so a rotated token doesn't re-probe (and briefly unmount the component).
    const bearerTokenRef = useRef(bearerToken);
    bearerTokenRef.current = bearerToken;

    useEffect(() => {
        if (!canProbe) {
            setAvailability('available');
            return undefined;
        }

        let cancelled = false;
        setAvailability('checking');
        fetch(`${apiOrigin}${probePath}`, {
            headers: { Accept: 'application/json', Authorization: `Bearer ${bearerTokenRef.current}` },
        })
            .then(isUnavailableResponse)
            .then((unavailable) => {
                if (!cancelled) setAvailability(unavailable ? 'unavailable' : 'available');
            })
            .catch(() => {
                if (!cancelled) setAvailability('available');
            });

        return () => {
            cancelled = true;
        };
    }, [apiOrigin, canProbe]);

    return availability;
};
